import { useState } from 'react';
import { formatRelativeTime } from '../../hooks/use-relative-time';
import styles from './BeliefTimeline.module.css';

type Trace = Readonly<{
  content?: string;
  description?: string;
}>;

type BeliefEvent = Readonly<{
  type: string;
  timestamp: string;
  description?: string;
  content?: string;
  belief?: Readonly<{ content?: string }>;
  oldBelief?: Readonly<{ content?: string }>;
  newBelief?: Readonly<{ content?: string }>;
  oldDescription?: string;
  newDescription?: string;
  evidenceTraces?: readonly Trace[];
  traces?: readonly Trace[];
  traceCount?: number;
  bridgingTraces?: readonly Trace[];
  peak?: Readonly<{ content?: string; significance?: string }>;
  title?: string;
  significance?: string;
}>;

type BeliefTimelineProps = Readonly<{
  events: readonly BeliefEvent[];
}>;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

const formatMonthKey = (timestamp: string): string => {
  const d = new Date(timestamp);
  return `${d.getFullYear()}-${String(d.getMonth()).padStart(2, '0')}`;
};

const formatMonthLabel = (timestamp: string): string => {
  const d = new Date(timestamp);
  return `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
};

const getTraces = (event: BeliefEvent): readonly Trace[] =>
  event.evidenceTraces ?? event.traces ?? [];

const isThinTimeline = (events: readonly BeliefEvent[]): boolean => {
  const beliefCount = events.filter(
    (e) => e.type === 'belief_formed' || e.type === 'belief_superseded'
  ).length;
  const hasSupersessions = events.some((e) => e.type === 'belief_superseded');
  return beliefCount <= 3 && !hasSupersessions;
};

const BeliefFormedEvent = ({
  event,
  expanded,
  onToggle,
}: Readonly<{
  event: BeliefEvent;
  expanded: boolean;
  onToggle: () => void;
}>): React.ReactElement => {
  const traces = getTraces(event);
  const traceCount = event.traceCount ?? traces.length;
  const beliefText = event.belief?.content ?? event.content ?? event.description ?? '';

  return (
    <div className={styles.eventBody}>
      <span className={styles.beliefContent} data-testid="belief-content">
        &ldquo;{beliefText}&rdquo;
      </span>
      {traceCount > 0 && (
        <>
          <div
            className={styles.traceToggle}
            onClick={onToggle}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                onToggle();
              }
            }}
            data-testid="trace-toggle"
          >
            Derived from {traceCount} trace{traceCount !== 1 ? 's' : ''}{' '}
            {expanded ? '\u25B4' : '\u25BE'}
          </div>
          <div
            className={`${styles.traceList} ${expanded ? styles.traceListExpanded : ''}`}
            data-testid="trace-list"
          >
            {traces.map((trace, idx) => (
              <div key={idx} className={styles.traceItem}>
                {trace.content ?? trace.description ?? ''}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const BeliefSupersededEvent = ({
  event,
}: Readonly<{ event: BeliefEvent }>): React.ReactElement => {
  const oldText = event.oldBelief?.content ?? event.oldDescription ?? '';
  const newText = event.newBelief?.content ?? event.newDescription ?? '';
  const bridgingCount = event.bridgingTraces?.length ?? 0;

  return (
    <div className={styles.eventBody}>
      <span className={styles.supersededContent} data-testid="old-belief">
        {oldText}
      </span>
      <div className={styles.newContent} data-testid="new-belief">
        &rarr; {newText}
      </div>
      {bridgingCount > 0 && (
        <div className={styles.meta}>
          {bridgingCount} bridging trace{bridgingCount !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

const PeakRecordedEvent = ({
  event,
}: Readonly<{ event: BeliefEvent }>): React.ReactElement => {
  const peakText = event.peak?.content ?? event.content ?? event.title ?? '';
  const significance = event.peak?.significance ?? event.significance;

  return (
    <div className={styles.eventBody}>
      <span className={styles.peakContent}>{peakText}</span>
      {significance != null && (
        <div className={styles.meta}>Significance: {significance}</div>
      )}
    </div>
  );
};

const DefaultEvent = ({
  event,
}: Readonly<{ event: BeliefEvent }>): React.ReactElement => (
  <div className={styles.eventBody}>
    {event.description ?? event.type}
  </div>
);

const EventItem = ({
  event,
  index,
}: Readonly<{
  event: BeliefEvent;
  index: number;
}>): React.ReactElement => {
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = (): void => {
    setExpanded((prev) => !prev);
  };

  return (
    <div className={styles.event} data-testid={`event-${index}`}>
      <div className={styles.eventDate}>{formatRelativeTime(event.timestamp)}</div>
      {event.type === 'belief_formed' && (
        <BeliefFormedEvent event={event} expanded={expanded} onToggle={toggleExpanded} />
      )}
      {event.type === 'belief_superseded' && (
        <BeliefSupersededEvent event={event} />
      )}
      {event.type === 'peak_recorded' && (
        <PeakRecordedEvent event={event} />
      )}
      {!['belief_formed', 'belief_superseded', 'peak_recorded'].includes(event.type) && (
        <DefaultEvent event={event} />
      )}
    </div>
  );
};

const BeliefTimeline = ({ events }: BeliefTimelineProps): React.ReactElement => {
  if (events.length === 0) {
    return (
      <div className={styles.emptyState} data-testid="empty-state">
        This entity&apos;s identity is emerging. Traces are accumulating &mdash; beliefs
        will form as consolidation discovers patterns.
      </div>
    );
  }

  const thin = isThinTimeline(events);

  if (thin) {
    return (
      <div className={styles.timeline} data-testid="belief-timeline">
        {events.map((event, idx) => (
          <EventItem key={idx} event={event} index={idx} />
        ))}
        <div className={styles.hint} data-testid="thin-hint">
          Early chapters. Belief evolution will appear as experiences challenge and
          refine these initial positions.
        </div>
      </div>
    );
  }

  // Full timeline: group by month
  const monthGroups: Array<{ key: string; label: string; events: Array<{ event: BeliefEvent; originalIndex: number }> }> = [];
  const monthMap = new Map<string, number>();

  events.forEach((event, idx) => {
    const key = formatMonthKey(event.timestamp);
    const existingIdx = monthMap.get(key);
    if (existingIdx != null) {
      monthGroups[existingIdx].events.push({ event, originalIndex: idx });
    } else {
      monthMap.set(key, monthGroups.length);
      monthGroups.push({
        key,
        label: formatMonthLabel(event.timestamp),
        events: [{ event, originalIndex: idx }],
      });
    }
  });

  return (
    <div className={styles.timeline} data-testid="belief-timeline">
      {monthGroups.map((group) => (
        <div key={group.key}>
          <div className={styles.monthMarker} data-testid="month-marker">
            {group.label}
          </div>
          {group.events.map(({ event, originalIndex }) => (
            <EventItem key={originalIndex} event={event} index={originalIndex} />
          ))}
        </div>
      ))}
    </div>
  );
};

export { BeliefTimeline };
export type { BeliefTimelineProps, BeliefEvent, Trace };
